import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { ChevronRight, Gavel, FileText } from 'lucide-react';

export default function EncheresConditions() {
  const { API } = useContext(AppContext);
  const [conditions, setConditions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConditions();
  }, []);

  const fetchConditions = async () => {
    try {
      const response = await axios.get(`${API}/encheres/conditions`);
      setConditions(response.data);
    } catch (error) {
      console.error('Error fetching conditions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Schema.org FAQPage for common questions about auction conditions
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Comment fonctionne la vente par courtage aux enchères ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Il s'agit d'une vente de gré à gré organisée par GEOTS. Les acheteurs inscrits peuvent enchérir sur les lots proposés. Le vendeur reste libre du choix de l'acquéreur et peut refuser l'ensemble des offres."
        }
      },
      {
        "@type": "Question",
        "name": "Quels sont les frais pour les acheteurs ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "L'inscription est gratuite. Des frais de dossier s'appliquent uniquement en cas d'attribution d'un lot. La commission est incluse dans le prix affiché."
        }
      },
      {
        "@type": "Question",
        "name": "Quels sont les frais pour les vendeurs ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le dépôt de lots est gratuit. Aucun droit d'entrée ni frais de dossier. La commission n'est due qu'en cas de vente effective."
        }
      },
      {
        "@type": "Question",
        "name": "Le vendeur est-il obligé d'accepter l'offre la plus élevée ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Non. Conformément à l'article L.321-3 du Code de commerce, il s'agit d'une vente de gré à gré. Le vendeur n'est pas tenu de retenir l'offre la plus élevée et peut refuser l'ensemble des offres."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://geots.consulting/" },
      { "@type": "ListItem", "position": 2, "name": "Enchères", "item": "https://geots.consulting/encheres" },
      { "@type": "ListItem", "position": 3, "name": "Conditions", "item": "https://geots.consulting/encheres/conditions" }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Conditions générales de vente par courtage aux enchères | GEOTS</title>
        <meta 
          name="description" 
          content="Conditions générales des ventes par courtage aux enchères de matériel géotechnique organisées par GEOTS Consulting. Inscription gratuite, commission au succès." 
        />
        <link rel="canonical" href="https://geots.consulting/encheres/conditions" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Conditions générales - Vente par courtage aux enchères" />
        <meta property="og:description" content="Conditions générales des ventes par courtage aux enchères de matériel géotechnique." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://geots.consulting/encheres/conditions" />
        <meta property="og:site_name" content="GEOTS Consulting" />
        
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Header */}
      <div className="bg-[#0A2540] text-white">
        <div className="container mx-auto px-4 py-12">
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-6">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/encheres" className="hover:text-white">Enchères</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">Conditions</span>
          </nav>

          <div className="flex items-center gap-4">
            <div className="bg-[#F0A22E]/20 p-3 rounded-lg">
              <FileText className="w-8 h-8 text-[#F0A22E]" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">
                Conditions générales de vente
              </h1>
              <p className="text-gray-300">Vente par courtage aux enchères</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#F0A22E]"></div>
            </div>
          ) : (
            <div className="prose prose-lg max-w-none">
              {conditions?.contenu_fr ? (
                <div dangerouslySetInnerHTML={{ __html: conditions.contenu_fr.replace(/\n/g, '<br/>').replace(/## /g, '<h2>').replace(/### /g, '<h3>') }} />
              ) : (
                <>
                  <h2>Article 1 - Nature de la vente</h2>
                  <p>
                    Les ventes organisées sur GEOTS sont des ventes de gré à gré par courtage aux enchères, 
                    régies par l&apos;article L.321-3 du Code de commerce. Il ne s&apos;agit pas de ventes aux enchères publiques.
                  </p>
                  <p>
                    GEOTS intervient en qualité de courtier et d&apos;hébergeur. Le contrat de vente est conclu 
                    directement entre le vendeur et l&apos;acquéreur retenu.
                  </p>

                  <h2>Article 2 - Absence d&apos;adjudication automatique</h2>
                  <p>
                    Le vendeur reste libre du choix de l&apos;acquéreur. Il n&apos;est pas tenu de retenir l&apos;offre 
                    la plus élevée et peut refuser l&apos;ensemble des offres sans avoir à justifier sa décision.
                  </p>
                  <p>
                    À l&apos;expiration du délai d&apos;enchères, aucune attribution automatique n&apos;a lieu. Le vendeur 
                    dispose d&apos;un délai de 5 jours ouvrés pour se prononcer sur les offres reçues.
                  </p>

                  <h2>Article 3 - Inscription préalable</h2>
                  <p>
                    La participation aux enchères est subordonnée à une inscription préalable et à la 
                    validation du dossier par GEOTS. Les pièces demandées sont :
                  </p>
                  <ul>
                    <li>Extrait Kbis de moins de 3 mois</li>
                    <li>Pièce d&apos;identité du dirigeant</li>
                    <li>Justificatif de financement ou attestation bancaire</li>
                  </ul>
                  <p>
                    Ces documents doivent être transmis par email avant la clôture des inscriptions.
                  </p>

                  <h2>Article 4 - Déroulement des enchères</h2>
                  <p>
                    Chaque offre doit être supérieure ou égale à la meilleure offre en cours augmentée 
                    du pas d&apos;enchère défini pour le lot. Les offres sont définitives et ne peuvent être 
                    ni modifiées, ni annulées.
                  </p>
                  <p>
                    <strong>Enchère glissante :</strong> Toute offre déposée dans les 2 dernières minutes 
                    repousse la clôture de 2 minutes.
                  </p>
                  <p>
                    <strong>Enchère automatique :</strong> Le participant peut définir un montant maximum. 
                    Le système enchérira automatiquement pour lui jusqu&apos;à ce plafond.
                  </p>

                  <h2>Article 5 - Commission</h2>
                  <p>
                    Une commission est incluse dans le prix affiché. Son taux est indiqué sur chaque vacation.
                    Elle est due uniquement en cas de vente effective.
                  </p>
                  <p>
                    Des frais de dossier peuvent être appliqués. Leur montant est indiqué sur chaque vacation.
                  </p>

                  <h2>Article 6 - Visite du matériel</h2>
                  <p>
                    Selon le lot, la visite peut être obligatoire, facultative ou sans objet :
                  </p>
                  <ul>
                    <li><strong>Visite obligatoire :</strong> L&apos;enchérisseur ne peut placer d&apos;offre qu&apos;après avoir effectué une visite confirmée par GEOTS.</li>
                    <li><strong>Visite facultative :</strong> La visite est possible sur rendez-vous mais n&apos;est pas requise pour enchérir.</li>
                    <li><strong>Sans visite :</strong> Le lot est vendu en l&apos;état sur la base des photos et documents fournis.</li>
                  </ul>

                  <h2>Article 7 - Paiement et enlèvement</h2>
                  <p>
                    Le paiement et l&apos;enlèvement du matériel se font directement entre le vendeur et 
                    l&apos;acquéreur retenu, selon les modalités convenues entre eux.
                  </p>
                  <p>
                    GEOTS n&apos;intervient pas dans le règlement financier et ne séquestre aucune somme.
                  </p>

                  <h2>Article 8 - Critères de sélection</h2>
                  <p>
                    Le vendeur retient l&apos;acquéreur en considérant notamment :
                  </p>
                  <ul>
                    <li>Le montant de l&apos;offre</li>
                    <li>La sécurité du financement</li>
                    <li>Le délai d&apos;enlèvement proposé</li>
                  </ul>
                  <p>
                    Ces critères sont indicatifs. Le vendeur reste souverain dans son choix.
                  </p>

                  <h2>Article 9 - Responsabilité</h2>
                  <p>
                    GEOTS agit en qualité d&apos;intermédiaire. La description des lots est rédigée par le vendeur.
                    GEOTS ne procède à aucune estimation de valeur et ne rédige aucun rapport d&apos;expertise.
                  </p>
                  <p>
                    L&apos;acquéreur est invité à vérifier l&apos;état du matériel lors de la visite ou, à défaut, 
                    à se fonder sur les documents fournis par le vendeur.
                  </p>

                  <h2>Article 10 - Données personnelles</h2>
                  <p>
                    Les données collectées sont utilisées pour la gestion des inscriptions et le bon 
                    déroulement des ventes. Elles ne sont communiquées au vendeur qu&apos;après la clôture 
                    des enchères, et uniquement pour les offres classées.
                  </p>
                  <p>
                    Conformément au RGPD, vous disposez d&apos;un droit d&apos;accès, de rectification et de 
                    suppression de vos données. Contact : contact@geots.consulting
                  </p>

                  <h2>Article 11 - Droit applicable</h2>
                  <p>
                    Les présentes conditions sont soumises au droit français. Tout litige relève de la 
                    compétence exclusive des tribunaux de Lyon.
                  </p>
                </>
              )}
            </div>
          )}

          <div className="mt-12 pt-8 border-t">
            <p className="text-sm text-gray-500 text-center">
              Dernière mise à jour : Août 2026
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[#0A2540] py-8">
        <div className="container mx-auto px-4 text-center">
          <Link to="/encheres" className="text-[#F0A22E] hover:underline">
            ← Retour aux enchères
          </Link>
        </div>
      </div>
    </div>
  );
}
